import {
  forwardRef,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { playClick } from "../lib/sounds";

function isSameOriginPathNavigation(to: string) {
  try {
    return new URL(to, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  replace?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, Props>(
  ({ to, replace, onMouseDown, onClick, ...props }, ref) => {
    const handleMouseDown = useCallback(
      (event: ReactMouseEvent<HTMLAnchorElement>) => {
        onMouseDown?.(event);
        if (event.defaultPrevented) return;
        if (!isSameOriginPathNavigation(to)) return;
        if (event.button !== 0 || event.ctrlKey || event.metaKey) return;

        playClick();
        event.preventDefault();
        const method = replace ? "replaceState" : "pushState";
        history[method](history.state, "", to);
      },
      [to, replace, onMouseDown]
    );

    // Pointer navigation is handled by handleMouseDown. This handler prevents
    // the browser's default navigation for those, and also handles
    // keyboard-triggered clicks (Enter on a focused link) which bypass
    // mousedown entirely — detail === 0 identifies them.
    const handleClick = useCallback(
      (event: ReactMouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (!isSameOriginPathNavigation(to)) return;
        if (event.button !== 0 || event.ctrlKey || event.metaKey) return;

        event.preventDefault();

        if (event.detail === 0) {
          playClick();
          const method = replace ? "replaceState" : "pushState";
          history[method](history.state, "", to);
        }
      },
      [to, replace, onClick]
    );

    return (
      <a
        ref={ref}
        href={to}
        {...props}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      />
    );
  }
);
